/**
 * SyncWarningDialog - Warning dialog for stopping active syncs
 *
 * Displays when a user attempts to save credential changes while
 * a connector is actively syncing, requiring confirmation before
 * stopping the sync.
 */

import React from 'react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

/**
 * Props for the SyncWarningDialog component
 */
export interface SyncWarningDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback when user closes the dialog without confirming */
  onClose: () => void;
  /** Callback when user confirms stopping the sync and saving */
  onConfirm: () => void;
  /** Name of the connector that is currently syncing */
  connectorName: string;
}

/**
 * SyncWarningDialog Component
 *
 * A confirmation dialog that appears when the user attempts to save
 * credential changes while the connector is actively syncing.
 * Warns that the sync will be stopped and must be restarted manually.
 */
export const SyncWarningDialog: React.FC<SyncWarningDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  connectorName,
}) => {
  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Stop Active Sync?</AlertDialogTitle>
          <AlertDialogDescription>
            The connector '{connectorName}' is currently syncing. Saving
            credential changes will stop the active sync. The sync will need to
            be restarted manually after the changes are saved.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button onClick={onConfirm} variant="destructive">
            Stop Sync & Save
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
